import React, { useState } from 'react';
import { UserCircle, X, SignIn, UserPlus, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const SignInForm = ({ onSwitchToRegister }) => {
  const { login, authError, isLoading } = useAuth();
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [localError, setLocalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    if (!loginEmail.trim() || !loginPassword) {
      setLocalError('Please provide both email and password.');
      return;
    }

    if (loginPassword.length < 8) {
      setLocalError('Password must be at least 8 characters long.');
      return;
    }

    const enc = new TextEncoder().encode(loginPassword);
    if (enc.length > 72) {
      setLocalError('Password exceeds maximum 72-byte limit.');
      return;
    }

    setSubmitting(true);
    try {
      await login({ email: loginEmail, password: loginPassword });
    } catch (_) {
      // Handled by context/toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="signin-form"
      onSubmit={handleSubmit}
      className="p-6 space-y-4"
    >
      {(localError || authError) && (
        <div className="p-3 bg-[#F85149]/10 border border-[#F85149]/30 rounded text-xs text-[#F85149] flex items-start gap-2">
          <WarningCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{localError || authError}</span>
        </div>
      )}

      <div>
        <label htmlFor="signin-email" className="block text-[11px] text-[#9CA3AF] uppercase mb-1">
          Email Address
        </label>
        <div className="relative">
          <input
            id="signin-email"
            type="email"
            name="email"
            autoComplete="username"
            required
            data-testid="auth-input-email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            placeholder="name@vasteon.io"
            className="w-full bg-[#0A0C10] border border-[#1F2937] focus:border-[#00E5FF] rounded px-3 py-2 text-xs text-[#F3F4F6] placeholder-[#4B5563] outline-none transition-all"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="signin-password" className="text-[11px] text-[#9CA3AF] uppercase">
            Password
          </label>
        </div>
        <div className="relative">
          <input
            id="signin-password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            data-testid="auth-input-password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full bg-[#0A0C10] border border-[#1F2937] focus:border-[#00E5FF] rounded px-3 py-2 text-xs text-[#F3F4F6] placeholder-[#4B5563] outline-none transition-all"
          />
        </div>
      </div>

      <div className="pt-2">
        <button
          type="submit"
          data-testid="auth-submit-btn"
          disabled={submitting || isLoading}
          className="w-full py-2.5 bg-[#00E5FF] hover:bg-[#00B4D8] text-black font-bold text-xs rounded transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting || isLoading ? (
            <span className="animate-pulse">Authenticating...</span>
          ) : (
            <>
              <SignIn className="w-4 h-4" />
              Sign In
            </>
          )}
        </button>
      </div>

      <div className="text-center pt-2 text-[11px] text-[#6B7280]">
        <span>
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="text-[#00E5FF] hover:underline font-semibold"
          >
            Register
          </button>
        </span>
      </div>
    </form>
  );
};

const RegisterForm = ({ onSwitchToLogin }) => {
  const { register, authError, isLoading } = useAuth();
  const [registerDisplayName, setRegisterDisplayName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [localError, setLocalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    if (!registerEmail.trim() || !registerPassword) {
      setLocalError('Please provide both email and password.');
      return;
    }

    if (registerPassword.length < 8) {
      setLocalError('Password must be at least 8 characters long.');
      return;
    }

    const enc = new TextEncoder().encode(registerPassword);
    if (enc.length > 72) {
      setLocalError('Password exceeds maximum 72-byte limit.');
      return;
    }

    setSubmitting(true);
    try {
      await register({
        email: registerEmail,
        password: registerPassword,
        displayName: registerDisplayName
      });
    } catch (_) {
      // Handled by context/toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="register-form"
      onSubmit={handleSubmit}
      className="p-6 space-y-4"
    >
      {(localError || authError) && (
        <div className="p-3 bg-[#F85149]/10 border border-[#F85149]/30 rounded text-xs text-[#F85149] flex items-start gap-2">
          <WarningCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{localError || authError}</span>
        </div>
      )}

      <div>
        <label htmlFor="register-name" className="block text-[11px] text-[#9CA3AF] uppercase mb-1">
          Display Name (Optional)
        </label>
        <div className="relative">
          <input
            id="register-name"
            type="text"
            name="name"
            autoComplete="name"
            data-testid="auth-input-display-name"
            value={registerDisplayName}
            onChange={(e) => setRegisterDisplayName(e.target.value)}
            placeholder="e.g. Lead Architect"
            className="w-full bg-[#0A0C10] border border-[#1F2937] focus:border-[#00E5FF] rounded px-3 py-2 text-xs text-[#F3F4F6] placeholder-[#4B5563] outline-none transition-all"
          />
        </div>
      </div>

      <div>
        <label htmlFor="register-email" className="block text-[11px] text-[#9CA3AF] uppercase mb-1">
          Email Address
        </label>
        <div className="relative">
          <input
            id="register-email"
            type="email"
            name="email"
            autoComplete="email"
            required
            data-testid="auth-input-email"
            value={registerEmail}
            onChange={(e) => setRegisterEmail(e.target.value)}
            placeholder="name@vasteon.io"
            className="w-full bg-[#0A0C10] border border-[#1F2937] focus:border-[#00E5FF] rounded px-3 py-2 text-xs text-[#F3F4F6] placeholder-[#4B5563] outline-none transition-all"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="register-password" className="text-[11px] text-[#9CA3AF] uppercase">
            Password
          </label>
          <span className="text-[10px] text-[#6B7280]">Min. 8 characters</span>
        </div>
        <div className="relative">
          <input
            id="register-password"
            type="password"
            name="new-password"
            autoComplete="new-password"
            required
            data-testid="auth-input-password"
            value={registerPassword}
            onChange={(e) => setRegisterPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full bg-[#0A0C10] border border-[#1F2937] focus:border-[#00E5FF] rounded px-3 py-2 text-xs text-[#F3F4F6] placeholder-[#4B5563] outline-none transition-all"
          />
        </div>
      </div>

      <div className="pt-2">
        <button
          type="submit"
          data-testid="auth-submit-btn"
          disabled={submitting || isLoading}
          className="w-full py-2.5 bg-[#00E5FF] hover:bg-[#00B4D8] text-black font-bold text-xs rounded transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting || isLoading ? (
            <span className="animate-pulse">Authenticating...</span>
          ) : (
            <>
              <UserPlus className="w-4 h-4" />
              Create Account
            </>
          )}
        </button>
      </div>

      <div className="text-center pt-2 text-[11px] text-[#6B7280]">
        <span>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-[#00E5FF] hover:underline font-semibold"
          >
            Sign In
          </button>
        </span>
      </div>
    </form>
  );
};

export const AuthModal = () => {
  const { isAuthModalOpen, closeAuthModal } = useAuth();
  const [tab, setTab] = useState('login'); // 'login' | 'register'

  if (!isAuthModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xs p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-[#13131A] border border-[#1F2937] rounded-lg shadow-2xl overflow-hidden flex flex-col font-mono">
        {/* Header */}
        <div className="px-6 py-4 bg-[#0A0C10] border-b border-[#1F2937] flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#00E5FF]">
            <UserCircle className="w-5 h-5" />
            <h2 className="text-sm font-bold tracking-wide">
              {tab === 'login' ? 'ACCOUNT SIGN IN' : 'CREATE ACCOUNT'}
            </h2>
          </div>
          <button
            onClick={closeAuthModal}
            className="p-1 text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[#1F2937] rounded transition-all"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Selector */}
        <div className="flex border-b border-[#1F2937] bg-[#0E1015]">
          <button
            type="button"
            data-testid="tab-login"
            onClick={() => setTab('login')}
            className={`flex-1 py-2.5 text-xs font-semibold flex items-center justify-center gap-2 border-b-2 transition-all ${
              tab === 'login'
                ? 'border-[#00E5FF] text-[#00E5FF] bg-[#13131A]'
                : 'border-transparent text-[#6B7280] hover:text-[#9CA3AF]'
            }`}
          >
            <SignIn className="w-4 h-4" />
            Sign In
          </button>
          <button
            type="button"
            data-testid="tab-register"
            onClick={() => setTab('register')}
            className={`flex-1 py-2.5 text-xs font-semibold flex items-center justify-center gap-2 border-b-2 transition-all ${
              tab === 'register'
                ? 'border-[#00E5FF] text-[#00E5FF] bg-[#13131A]'
                : 'border-transparent text-[#6B7280] hover:text-[#9CA3AF]'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            Register
          </button>
        </div>

        {/* Distinct Form Trees */}
        {tab === 'login' ? (
          <SignInForm onSwitchToRegister={() => setTab('register')} />
        ) : (
          <RegisterForm onSwitchToLogin={() => setTab('login')} />
        )}
      </div>
    </div>
  );
};

export default AuthModal;
